 
import {  Injectable } from '@angular/core';
import {
  catchError,
  Observable,
  BehaviorSubject,
  ReplaySubject,
  from,
  switchMap,
  retry,
  filter,
  take,
  of,
  map,
  timer,
  timeout,
  throwError,
  finalize,
} from 'rxjs';
import { environment } from '../../environments/environment';
import {HttpClient,  HttpHeaders, HttpErrorResponse} from '@angular/common/http';
import { handleErrorFunction } from '../shared/helpers/handleError';
import { JobContact } from '../models/contacts/JobContact';
import { EmployeeContact } from '../models/contacts/EmployeeContact';
import {
  EmployeeDirectoryError,
  EmployeeDirectoryErrorReason,
} from '../models/contacts/EmployeeDirectoryError';
import { ContactInsight } from '../models/poData/ContactInsight';
import {
  buildEmployeeIndex,
  extractEmployeeEmails,
  searchEmployees,
} from '../domain/receiving-log/rules/employee-directory.rules';
import { resolveContactName } from '../domain/receiving-log/rules/contact-insight.rules';
import { EntraService } from './entra.service';
import { ToastService } from './toast.service';


/**
 * ContactsService owns the company employee directory: fetching it, caching it
 * for the life of the app session, and answering questions about it.
 *
 * It integrates with EntraService for authentication and uses BehaviorSubjects to
 * share the fetched data across components. It backs the employee drop-downs in
 * both UIs — AddContactComponent in the desktop LogFormComponent, and the "add
 * employee" search in the mobile routing step — plus every place a contact is
 * shown by name rather than by address.
 *
 * `GET /V1/employee-emails` returns {@link EmployeeContact} objects. Both forms
 * are kept: `fetchedEmployees` (the full records, which is what resolves names)
 * and `fetchedCompanyEmails` (just the addresses, which is all most callers need).
 */


@Injectable({
    providedIn: 'root'
  })
  export class ContactsService {
  
  
    private _httpClient: HttpClient;
    private _entraService: EntraService;
    private _toastService: ToastService;
   
  //  public fetchedJobContacts = new BehaviorSubject<JobContact[]>(new Array<JobContact>());

  // BehaviorSubjects to manage and share state across components:
    /**
     * The full directory records — names as well as addresses. Emitted alongside
     * `fetchedCompanyEmails`, so a subscriber to either sees a consistent pair.
     */
    public fetchedEmployees = new BehaviorSubject<EmployeeContact[]>(new Array<EmployeeContact>());
    /** Just the addresses, derived from `fetchedEmployees`. */
    public fetchedCompanyEmails = new BehaviorSubject<string[]>(new Array<string>());
    public isLoadingEmails = new BehaviorSubject<boolean>(false);

    /**
     * The last employee-directory fetch failure, or `null`.
     *
     * `null` means "nothing is wrong right now" — which covers **two** states:
     * the directory loaded successfully, and the directory has never been
     * fetched. They are not the same thing, and this subject cannot tell them
     * apart on its own. Disambiguate with `fetchedEmployees.value.length`:
     *
     * - error `null`, length > 0  → loaded, all good.
     * - error `null`, length 0    → never attempted (or still in flight; see
     *   `isLoadingEmails`).
     * - error set                 → the last attempt failed; `reason` says why
     *   and `message` is the text already shown to the user in a toast.
     *
     * Cleared at the start of every new attempt, and on success.
     */
    public emailsError = new BehaviorSubject<EmployeeDirectoryError | null>(null);

    /**
     * How long `ensureCompanyEmails()` waits for Entra to report a signed-in
     * user before giving up with a `timeout` failure. Long enough to cover a
     * cold token acquire plus the HTTP retries, short enough that nobody sits
     * in front of an unexplained spinner.
     */
    private static readonly SIGN_IN_WAIT_MS = 20000;

    /**
     * The current fetch, shared so overlapping callers join one HTTP request
     * instead of each firing their own. Null when nothing is in flight.
     */
    private _inFlight$: Observable<string[]> | null = null;

    /** Email → directory row, rebuilt on each successful fetch. See `findEmployee`. */
    private _employeeIndex = new Map<string, EmployeeContact>();

    /**
     * True once a fetch has come back with a usable list. Distinct from
     * `fetchedCompanyEmails.value.length` because it is what stops a second
     * caller from re-requesting: the list is fetched once per app session and
     * every page after that reads it out of the BehaviorSubject.
     */
    private _emailsLoaded = false;

    /** True while a request is in flight, so overlapping callers share one HTTP call. */
    private _emailsInFlight = false;


    constructor(private http: HttpClient,
        entraService:EntraService,
        toastService: ToastService
        ) {
      this._httpClient = http;

      this._entraService = entraService;
      this._toastService = toastService;
    }




/**
 * Fires a directory fetch and drops the result into the service's subjects.
 *
 * Kept for callers that just want the fetch to happen and do not want to
 * handle the outcome themselves. Anything that needs to know whether the
 * fetch worked should use {@link ensureCompanyEmails} or
 * {@link refreshCompanyEmails}, which report failure by erroring.
 *
 * Failures are toasted and recorded on `emailsError` either way.
 * @returns {void}
 */
  public fetchCompanyEmails(): void {
    this.runFetch().subscribe({
      error: (err: EmployeeDirectoryError) =>
        console.error('[contacts] employee directory fetch failed:', err),
    });
  }

/**
 * The single place a directory fetch is started, its outcome applied to the
 * service's state, and its failure reported to the user.
 *
 * The request is started here rather than on subscription, and its outcome is
 * replayed through a ReplaySubject, so overlapping callers join the one request
 * and a caller that never subscribes cannot leave the loading flag stuck on.
 *
 * @returns An observable that emits the loaded address list once and completes,
 *   or errors with an {@link EmployeeDirectoryError}.
 */
  private runFetch(): Observable<string[]> {
    if (this._inFlight$) {
      return this._inFlight$;
    }

    this._emailsInFlight = true;
    this.isLoadingEmails.next(true);
    this.emailsError.next(null);

    const result$ = new ReplaySubject<string[]>(1);
    // Held locally as well as on the field: a failure that happens synchronously
    // (no token) clears the field in `finalize` before this method returns.
    const inFlight$ = result$.asObservable();
    this._inFlight$ = inFlight$;

    this.GetCompanyEmails().pipe(
      map((results) => {
        const employees = results ?? [];
        // An empty list is not a cacheable answer — it is what an unauthenticated
        // request returns, so leave the cache open for the next caller to retry.
        // The token guard in GetCompanyEmails() now stops most of those before
        // they are sent, but the rule costs nothing and still holds.
        this._emailsLoaded = employees.length > 0;
        this._employeeIndex = buildEmployeeIndex(employees);
        // Records first, then addresses: a component that reacts to the address
        // list can look names up in the same tick.
        this.fetchedEmployees.next(employees);
        const emails = extractEmployeeEmails(employees);
        this.fetchedCompanyEmails.next(emails);
        this.emailsError.next(null);
        // Cleared here rather than only in `finalize`, which runs after the
        // value has already reached subscribers — a subscriber must not see
        // a settled result while the loading flag still says "in progress".
        this.clearInFlight();
        return emails;
      }),
      // Every failure is announced — not just 401. A silent directory is what
      // made this look like it "wasn't held onto" rather than a failed fetch.
      catchError((err: unknown) => {
        this.clearInFlight();
        return throwError(() => this.reportFailure(this.asDirectoryError(err)));
      }),
      // Backstop for an unsubscribe that never produced either outcome.
      finalize(() => this.clearInFlight())
    ).subscribe({
      next: (emails) => result$.next(emails),
      error: (err) => result$.error(err),
      complete: () => result$.complete(),
    });

    return inFlight$;
  }

/** Marks the current fetch finished. Safe to call more than once. */
  private clearInFlight(): void {
    this._emailsInFlight = false;
    this._inFlight$ = null;
    if (this.isLoadingEmails.value) {
      this.isLoadingEmails.next(false);
    }
  }

/**
 * The one entry point every page should use to get employee emails.
 *
 * Fetches at most once per app session: returns the cached list immediately if
 * it is already loaded, joins the in-flight request if one is running, and
 * otherwise waits for Entra to report a signed-in user before requesting.
 *
 * That last part is the reason this exists. `EntraService.acquireToken()`
 * returns `of(undefined)` when MSAL has no active account yet. Waiting on
 * `userIsLoggedIn` means the app-load prefetch actually lands, and every page
 * after that is a cache read.
 *
 * **This observable always terminates.** It emits the address list and
 * completes, or it errors with an {@link EmployeeDirectoryError} — it never
 * parks indefinitely. Two things used to make it hang: a failed fetch pushed
 * nothing to `fetchedCompanyEmails`, and a stuck MSAL interaction meant
 * `userIsLoggedIn` never turned true. Both now end in an error, and both are
 * toasted. Callers should pass an `error` handler.
 *
 * @returns {Observable<string[]>} Emits the loaded list once, then completes;
 *   errors with an {@link EmployeeDirectoryError} on failure.
 */
  public ensureCompanyEmails(): Observable<string[]> {
    if (this._emailsLoaded) {
      return of(this.fetchedCompanyEmails.value);
    }

    if (this._inFlight$) {
      return this._inFlight$;
    }

    return this._entraService.userIsLoggedIn.pipe(
      filter((loggedIn) => loggedIn),
      take(1),
      timeout({ first: ContactsService.SIGN_IN_WAIT_MS }),
      catchError(() => throwError(() => this.reportFailure(
        this.buildError('timeout', null, 'Timed out waiting for sign-in.')
      ))),
      switchMap(() => {
        // Re-check: another caller may have started (or finished) the fetch
        // while this one was parked waiting for sign-in.
        if (this._emailsLoaded) {
          return of(this.fetchedCompanyEmails.value);
        }
        return this.runFetch();
      })
    );
  }

/**
 * Forces a fresh fetch of the employee directory, ignoring the cache.
 *
 * Use this when the directory is known or suspected to be stale or missing —
 * a retry offered after a failure, or a refresh before an action that depends
 * on the list being current. {@link ensureCompanyEmails} is still the right
 * call for "make sure this is loaded"; this one always goes back to the API.
 *
 * Semantics:
 * - Ignores `_emailsLoaded`, so a populated cache does not short-circuit it.
 * - Clears `emailsError` before starting.
 * - Safe to call while a request is already in flight: it joins that request
 *   rather than firing a second one.
 * - Does **not** wait for sign-in. If there is no usable token the result is a
 *   `no-token` failure straight away, rather than a wait — a manual refresh
 *   should report, not park.
 * - Leaves the existing cached list in place unless and until a new one
 *   arrives, so a failed refresh does not blank out a working directory.
 *
 * @returns {Observable<string[]>} The same contract as
 *   {@link ensureCompanyEmails}: emits the list once and completes, or errors
 *   with an {@link EmployeeDirectoryError}.
 */
  public refreshCompanyEmails(): Observable<string[]> {
    if (this._inFlight$) {
      return this._inFlight$;
    }

    this._emailsLoaded = false;
    this.emailsError.next(null);
    return this.runFetch();
  }

/**
 * Looks up a directory row by email address (case-insensitive).
 *
 * @param email - The address to look up.
 * @returns The employee record, or undefined for an address the directory has no
 *   row for — an external contact, a group / distribution list, or a directory
 *   that hasn't loaded yet. Callers fall back rather than treat this as an error.
 */
  public findEmployee(email: string | null | undefined): EmployeeContact | undefined {
    if (!email) { return undefined; }
    return this._employeeIndex.get(email.trim().toLowerCase());
  }

/**
 * Ensures the directory has records, forcing a fresh fetch when it does not.
 *
 * `ensureCompanyEmails()` only fetches once per app session — a transient
 * failure at app load leaves `fetchedEmployees` empty for the rest of that
 * visit, since nothing else re-attempts it. This is the retry: callers that
 * are about to rely on the directory (starting or submitting a log) call
 * this instead, keyed on `fetchedEmployees.value.length === 0` rather than
 * staleness, so a populated cache is never re-fetched.
 *
 * @returns {Observable<string[]>} The same contract as {@link ensureCompanyEmails}:
 *   emits the addresses once and completes, or errors with an
 *   {@link EmployeeDirectoryError}.
 */
  public ensureDirectoryPopulated(): Observable<string[]> {
    if (this.fetchedEmployees.value.length > 0) {
      return of(this.fetchedCompanyEmails.value);
    }
    return this.refreshCompanyEmails();
  }

/**
 * The name to show or print for a contact — the app's single entry point for
 * turning an address into a person's name.
 *
 * Resolves the directory row for the address and hands it to
 * {@link resolveContactName}, so the preferred-name-plus-last-name from the
 * directory wins, the PO's own BambooHR insight covers anyone the directory
 * doesn't list, and a name picked out of the address remains the last resort.
 *
 * @param email - The contact's email address.
 * @param insight - The contact's insight from the PO fetch, when there is one.
 * @returns A display name; '' only when there is no name and no email.
 */
  public resolveName(email: string, insight?: ContactInsight): string {
    return resolveContactName(email, insight, this.findEmployee(email));
  }

/**
 * Directory search for the employee pickers, over the cached list.
 *
 * @param query - Raw text from the search box.
 * @param excludeEmails - Addresses already chosen, filtered out of the results.
 * @returns Up to ten matching employees.
 */
  public searchDirectory(query: string, excludeEmails: string[] = []): EmployeeContact[] {
    return searchEmployees(this.fetchedEmployees.value, query, excludeEmails);
  }






// ***************************** HTTP Requests *****************************
    
/**
 * Fetches the company employee directory from the backend API.
 * Acquires an access token from `EntraService` before making the request.
 *
 * No token means no request: `acquireToken()` emits `undefined` for a session
 * with no active account, and interpolating that produced a literal
 * `Bearer undefined` header — a guaranteed 401 that was then retried three
 * more times. That case now fails immediately with a `no-token` error.
 *
 * Returns active employees that have a BambooHR work email (filtered server-side),
 * as objects carrying the address plus the person's names. It previously returned
 * a flat `string[]` of addresses — see {@link EmployeeContact}.
 *
 * @returns {Observable<EmployeeContact[]>} An observable of directory records,
 *   or an error of type {@link EmployeeDirectoryError}.
 */
public GetCompanyEmails( ): Observable<EmployeeContact[]> {

    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
        if (!accessToken) {
          return throwError(() =>
            this.buildError('no-token', null, 'No access token available.')
          );
        }

        const url = `${environment.apiUrl}/V1/employee-emails`;
        const headers: HttpHeaders = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`,
        });

        return this._httpClient.get<EmployeeContact[]>(url, { headers }).pipe(
          retry({
            count: 2,
            // Only retry what a retry could fix. A 401/403 will be rejected
            // exactly the same way three more times; retrying it only adds
            // seconds to a failure that has already happened.
            delay: (error: HttpErrorResponse) =>
              this.isRetryable(error) ? timer(1000) : throwError(() => error),
          }),
          catchError((error: HttpErrorResponse) => this.toDirectoryError(error))
        );
      })
    );
}

/** Network drop-outs and server faults are worth another attempt; auth failures are not. */
private isRetryable(error: HttpErrorResponse): boolean {
  return error?.status === 0 || error?.status >= 500;
}

/**
 * Converts an HTTP failure into an {@link EmployeeDirectoryError}, reusing
 * `handleErrorFunction` for its logging and message extraction rather than
 * changing it — it is shared with many other services.
 */
private toDirectoryError(error: HttpErrorResponse): Observable<never> {
  const status = error?.status;
  let reason: EmployeeDirectoryErrorReason;
  if (status === 401 || status === 403) {
    reason = 'unauthorized';
  } else if (status === 0) {
    reason = 'network';
  } else {
    reason = 'server';
  }

  return handleErrorFunction(error).pipe(
    catchError((wrapped: any) =>
      throwError(() =>
        this.buildError(reason, wrapped?.details ?? null, wrapped?.message ?? '', error)
      )
    )
  );
}

/**
 * Normalises whatever reached the fetch's error handler into an
 * {@link EmployeeDirectoryError}. Anything already typed passes through; an
 * unexpected stream error is treated as a server failure so the user still
 * gets told something rather than nothing.
 */
private asDirectoryError(err: unknown): EmployeeDirectoryError {
  const candidate = err as Partial<EmployeeDirectoryError> | null;
  if (candidate && typeof candidate.reason === 'string' && candidate.message) {
    return candidate as EmployeeDirectoryError;
  }
  return this.buildError(
    'server',
    (candidate as any)?.details ?? null,
    (candidate as any)?.message ?? String(err),
    err
  );
}

/** Builds the typed failure, including the wording the user will actually see. */
private buildError(
  reason: EmployeeDirectoryErrorReason,
  details: any,
  technicalMessage: string,
  originalError: unknown = null
): EmployeeDirectoryError {
  return {
    reason,
    message: ContactsService.MESSAGES[reason],
    details,
    technicalMessage,
    originalError,
  };
}

/** Records a failure on `emailsError` and tells the user about it. Returns the failure. */
private reportFailure(failure: EmployeeDirectoryError): EmployeeDirectoryError {
  console.error('[contacts] employee directory unavailable:', failure);
  this.emailsError.next(failure);
  this._toastService.showWarningMessage(failure.message, 6000);
  return failure;
}

/**
 * What the user is told, per reason. Worded for a receiving clerk, and
 * deliberately distinct per reason so a support call can identify which
 * failure happened from the message alone.
 */
private static readonly MESSAGES: Record<EmployeeDirectoryErrorReason, string> = {
  'no-token':
    'You are signed out, so the employee list could not be loaded. Refresh the page and sign in again.',
  unauthorized:
    'Your sign-in has expired, so the employee list could not be loaded. Refresh the page and sign in again.',
  network:
    'The employee list could not be loaded because there is no connection to the server. Check your network and try again.',
  server:
    'The employee list could not be loaded because the server returned an error. Try again in a moment.',
  timeout:
    'Signing in is taking too long, so the employee list could not be loaded. Refresh the page and try again.',
};





/*These methods are not currently being used. They are based off of the previous desktop one-click app*/


// /* The API endpoint is not currently being used. They are based off of the previous desktop one-click app*/
//   public fetchJobContacts(jobNumber:string): void {
//     console.log('jobNumber in fetchJobContacts', jobNumber);
//     if (jobNumber !== ''&& jobNumber !== null) {
//     this.GetJobContacts(jobNumber).subscribe({
//       next: (results) => {
//         this.fetchedJobContacts.next(results);
//         console.log("results in fetchJobContacts", results)
//       },
//       error: (err: HttpErrorResponse) => {
//         console.error(err);
// console.log("Error in fetchJobContacts")
//       }
//     });
//   }
//   }


//     public GetJobContacts(jobNumber:string ): Observable<JobContact[]> {

//         const url = `${environment.apiUrl}/V1/job-contacts/${jobNumber}`;
//         console.log('url in GetJobContacts', url);
//         const headers: HttpHeaders = new HttpHeaders({
//           Authorization: `Bearer ${this._accessToken}`,
//         });
    
//         return this._httpClient
//         .get<JobContact[]>(url, { headers: headers })
//         .pipe(catchError(this.handleError));
//     }

  
}